import { useForm } from 'react-hook-form';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Chip, Alert, Button } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { CONFIG } from 'src/config-global';
import { varAlpha } from 'src/theme/styles';
import { useGetProfile } from 'src/modules/profile/services';
import { useVirtualLogin } from 'src/contexts/virtual-login-context';
import { useDocumentOperations } from 'src/api/services/zerreactor_backend_definition/zerreactorBackendService';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { AnimateAvatar } from 'src/components/animate';

import { useAuthContext } from 'src/auth/hooks';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';


// ----------------------------------------------------------------------

export function AccountDrawer({ data = [], sx, ...other }) {
  const theme = useTheme();

  const { user, changeOrganization } = useAuthContext()
  const { isVirtualLogin, virtualUser, originalAdmin, exitVirtualLogin, loading } = useVirtualLogin();
  const { getDocumentById } = useDocumentOperations();
  
  // Get fresh profile data from Auth/Profile API for better profile picture handling
  const { userSelf: profileData, userSelfLoading: profileLoading } = useGetProfile();
  
  const defaultOrganization = user?.UserOrganizations?.find(org => org.IsDefault);

  const [currentOrganizationId, setCurrentOrganizationId] = useState(defaultOrganization?.OrganizationId);

  // Profile image state
  const [profileImageUrl, setProfileImageUrl] = useState('/assets/placeholder.svg');
  const [imageExpired, setImageExpired] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageExpiryTime, setImageExpiryTime] = useState(null);
  const imageLoadedRef = useRef(false);
  const getDocumentByIdRef = useRef(getDocumentById);

  // Update ref when getDocumentById changes
  useEffect(() => {
    getDocumentByIdRef.current = getDocumentById;
  }, [getDocumentById]);

  // Function to check if image is expired
  const isImageExpired = useCallback(() => {
    if (!imageExpiryTime) return false;
    const currentTime = Date.now();
    const isExpired = currentTime >= imageExpiryTime;
    
    if (isExpired) {
      // console.log('Profile image has expired. Current time:', new Date(currentTime).toISOString(), 'Expiry time:', new Date(imageExpiryTime).toISOString());
    }
    
    return isExpired;
  }, [imageExpiryTime]);



  
  const methods = useForm({
    defaultValues: {
      organizationId: currentOrganizationId
    }
  })

  const router = useRouter();

  const pathname = usePathname();

  const [open, setOpen] = useState(false);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickItem = useCallback(
    (path) => {
      handleCloseDrawer();
      router.push(path);
    },
    [handleCloseDrawer, router]
  );

  const handleOrganizationChange = useCallback(async (event) => {
    const selectedOrgId = event.target.value;
    try {
      await changeOrganization(selectedOrgId);
      setCurrentOrganizationId(selectedOrgId);
      handleCloseDrawer();
    } catch (error) {
      console.error("Error switching organization:", error);
    }
  }, [handleCloseDrawer, changeOrganization]);

  const handleExitVirtualLogin = useCallback(() => {
    exitVirtualLogin();
    handleCloseDrawer();
  }, [exitVirtualLogin, handleCloseDrawer]);

  // Use profile data from Auth/Profile API for normal users, fallback to user context for virtual login
  // Virtual login uses the injected virtualUser, normal users use fresh profile data
  const displayUser = isVirtualLogin ? virtualUser : (profileData || user);
  


  // Function to load profile image
  const loadProfileImage = useCallback(async () => {
    setLoadingImage(true);
    try {
      // Support both new ProfilePicture format (from Auth/Profile) and old FaceImage format
      const documentId = displayUser.ProfilePicture?.Id || displayUser.FaceImage?.Id;
      const documentData = await getDocumentByIdRef.current(documentId);
      // console.log("documentData", documentData);
      if (documentData && documentData.ServerAccess && documentData.ServerAccess.ShareUrl) {
        // Build full URL from ShareUrl and server URL
        const fullUrl = `${CONFIG.site.serverUrl}${documentData.ServerAccess.ShareUrl}`;
        setProfileImageUrl(fullUrl);
        setImageExpired(false);
        
        // Store expiry time for future checks
        if (documentData.ServerAccess.ExpireDateInMilliseconds) {
          setImageExpiryTime(documentData.ServerAccess.ExpireDateInMilliseconds);
        }
        
        imageLoadedRef.current = true; // Mark as loaded so we don't fetch again
      } else {
        setProfileImageUrl('/assets/placeholder.svg');
        setImageExpired(true);
        setImageExpiryTime(null);
      }
    } catch (error) {
      console.error('Error loading profile image:', error);
      setProfileImageUrl('/assets/placeholder.svg');
      setImageExpired(true);
      setImageExpiryTime(null);
    } finally {
      setLoadingImage(false);
    }
  }, [displayUser]);

  // Load profile image when user changes
  useEffect(() => {
    // Always reset ALL state when displayUser changes
    imageLoadedRef.current = false;
    setLoadingImage(false);
    
    // Immediately clear profile image to prevent showing previous user's image
    setProfileImageUrl('/assets/placeholder.svg');
    setImageExpired(false);
    setImageExpiryTime(null);
    
    const profileImageId = displayUser?.ProfilePicture?.Id || displayUser?.FaceImage?.Id;
    if (!profileImageId) {
      return;
    }

    loadProfileImage();
  }, [displayUser, loadProfileImage]);

  // Additional useEffect to watch for user context changes specifically
  useEffect(() => {
    imageLoadedRef.current = false;
    setProfileImageUrl('/assets/placeholder.svg');
    setImageExpired(false);
    setImageExpiryTime(null);
  }, [user?.Id]);

  // Function to refresh expired image
  const refreshExpiredImage = useCallback(async () => {
    const profileImageId = displayUser?.ProfilePicture?.Id || displayUser?.FaceImage?.Id;
    if (isImageExpired() && profileImageId) {
      // console.log('Profile image expired, refreshing...');
      imageLoadedRef.current = false; // Reset loaded flag to force refresh
      await loadProfileImage();
    }
  }, [isImageExpired, displayUser?.ProfilePicture?.Id, displayUser?.FaceImage?.Id, loadProfileImage]);

  // Monitor image expiry and refresh when needed
  useEffect(() => {
    if (!imageExpiryTime) return undefined;

    const checkExpiry = () => {
      if (isImageExpired()) {
        refreshExpiredImage();
      }
    };

    // Check immediately
    checkExpiry();

    // Set up interval to check every minute
    const interval = setInterval(checkExpiry, 60000);

    return () => clearInterval(interval);
  }, [imageExpiryTime, isImageExpired, refreshExpiredImage]);

  const displayName = `${displayUser?.FirstName || ''} ${displayUser?.LastName || ''}`.trim();

  const renderAvatar = (
    <AnimateAvatar
      width={96}
      slotProps={{
        avatar: { 
          alt: displayUser?.FirstName,
          src: profileImageUrl !== '/assets/placeholder.svg' ? profileImageUrl : undefined,
          onError: () => {
            // console.log('Profile image failed to load, attempting refresh...');
            setProfileImageUrl('/assets/placeholder.svg');
            setImageExpired(true);
            // Try to refresh the image if it failed to load
            const profileImageId = displayUser?.ProfilePicture?.Id || displayUser?.FaceImage?.Id;
            if (profileImageId) {
              imageLoadedRef.current = false;
              loadProfileImage();
            }
          }
        },
        overlay: {
          border: 2,
          spacing: 3,
          color: isVirtualLogin 
            ? `linear-gradient(135deg, ${varAlpha(theme.vars.palette.success.mainChannel, 0)} 25%, ${theme.vars.palette.success.main} 100%)`
            : `linear-gradient(135deg, ${varAlpha(theme.vars.palette.primary.mainChannel, 0)} 25%, ${theme.vars.palette.primary.main} 100%)`,
        },
      }}
    >
      {displayUser?.FirstName?.charAt(0).toUpperCase()}
    </AnimateAvatar>
  );

  return (
    <>
      <AccountButton
        open={open}
        onClick={handleOpenDrawer}
        displayName={displayUser?.FirstName}
        profileImageUrl={profileImageUrl}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={handleCloseDrawer}
        anchor="right"
        slotProps={{ 
          backdrop: { invisible: true },
          root: { inert: open ? undefined : '' }
        }}
        PaperProps={{ sx: { width: 320 } }}
      >
        <IconButton
          onClick={handleCloseDrawer}
          sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Stack alignItems="center" sx={{  pt:8}}>
            {renderAvatar}

            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
              {displayName}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {displayUser?.Email}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {displayUser?.RoleName}
            </Typography>

            {isVirtualLogin && (
              <Stack spacing={1.5} alignItems="center" sx={{ mt: 2, px: 2, width: '100%' }}>
                <Chip
                  icon={<Iconify icon="mdi:account-switch" width={16} />}
                  label="Virtual Login Active"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                
                <Alert 
                  severity="info" 
                  sx={{ 
                    width: '100%',
                    fontSize: '0.75rem',
                    '& .MuiAlert-message': { 
                      textAlign: 'center',
                      width: '100%' 
                    }
                  }}
                >
                  Acting as {virtualUser?.FirstName} {virtualUser?.LastName}
                  <br />
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Original: {originalAdmin?.FirstName} {originalAdmin?.LastName}
                  </Typography>
                </Alert>

                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  fullWidth
                  startIcon={<Iconify icon="eva:log-out-fill" />}
                  onClick={handleExitVirtualLogin}
                  loading={loading}
                  sx={{ mt: 1, fontWeight: 600 }}
                >
                  Exit Virtual Login
                </Button>
              </Stack>
            )}

          </Stack>

          <Stack
            sx={{
              py: 3,
              px: 2.5,
              borderTop: `dashed 1px ${theme.vars.palette.divider}`,
              borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
            }}
          >
            {data.map((option) => {
              const rootLabel = 'Home';
              const rootHref = paths.dashboard.root;

              return (
                <MenuItem
                  key={option.label}
                  onClick={() => handleClickItem(option.label === 'Home' ? rootHref : option.href)}
                  sx={{
                    py: 1,
                    color: 'text.secondary',
                    '& svg': { width: 24, height: 24 },
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {option.icon}

                  <Box component="span" sx={{ ml: 2 }}>
                    {option.label === 'Home' ? rootLabel : option.label}
                  </Box>

                  {option.info && (
                    <Label color="error" sx={{ ml: 1 }}>
                      {option.info}
                    </Label>
                  )}
                </MenuItem>
              );
            })}
          </Stack>

        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={handleCloseDrawer} />
        </Box>
      </Drawer>
    </>
  );
}
